import useQueryParams from 'hooks/useQueryParams'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { replace } from 'connected-react-router'
import { useAppDispatch } from 'hooks/reduxHooks'

const EXPERIENCES_SORT_BY_TYPE = 'sortBy'

export default function useExperiencesSortOptions(defaultSortBy: Experiences.SortTypes = 'type') {
  const intl = useIntl()
  const appDispatch = useAppDispatch()
  const queryParameters = useQueryParams()

  const SORT_OPTIONS: Utils.PartialRecord<Experiences.SortTypes, { label: string }> = useMemo(() => ({
    type: {
      label: intl.formatMessage({
        id: 'Teg7ezl0L0',
        defaultMessage: 'Recommended',
        description: 'Sort option label for recommended experiences',
      }),
    },
    latest: {
      label: intl.formatMessage({
        id: '2PIynFTqWf',
        defaultMessage: 'Latest',
        description: 'Sort option label for latest experiences',
      }),
    },
    endingsoon: {
      label: intl.formatMessage({
        id: 'Vikvsg6gkY',
        defaultMessage: 'Ending Soon',
        description: 'Sort option label for ending soon experiences',
      }),
    },
    'price.asc': {
      label: intl.formatMessage({
        id: 'HjpspgClft',
        defaultMessage: 'Price (Lowest)',
        description: 'Sort option label for lowest price experiences',
      }),
    },
    'price.desc': {
      label: intl.formatMessage({
        id: '9Cw0Z3nVku',
        defaultMessage: 'Price (Highest)',
        description: 'Sort option label for highest price experiences',
      }),
    },
  }), [intl])
  const sortOptionFromUrl = queryParameters.get(EXPERIENCES_SORT_BY_TYPE)

  const sortBy = useMemo<Experiences.SortTypes>(() => {
    if (
      !sortOptionFromUrl ||
      !SORT_OPTIONS[sortOptionFromUrl as Experiences.SortTypes]
    ) return defaultSortBy
    return sortOptionFromUrl as Experiences.SortTypes
  }, [defaultSortBy, sortOptionFromUrl, SORT_OPTIONS])

  const setSortBy = useCallback((newSortBy: Experiences.SortTypes) => {
    const newUrlFilters = convertSortToUrl(newSortBy, queryParameters)
    appDispatch(replace({
      search: newUrlFilters.toString(),
    }))
  }, [appDispatch, queryParameters])

  const sortOptions = SORT_OPTIONS

  return {
    sortBy,
    setSortBy,
    sortOptions,
  }
}

function convertSortToUrl(newSortBy: Experiences.SortTypes, currentSearchParams: URLSearchParams) {
  const searchParams = new URLSearchParams(currentSearchParams)
  if (newSortBy) {
    searchParams.set(EXPERIENCES_SORT_BY_TYPE, String(newSortBy))
  }
  return searchParams
}
